import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../App.css';
import './AddBookForm.css';

const DisclaimerPopup = ({ userData }) => {
  const email = userData?.email || "Guest";
  const logOut = () => {
    localStorage.clear();
    window.location.href = "/admin";
  };

  return (
    <div className="user-profile-bar">
      <div className="user-info">
        <div className="user-avatar">{email.charAt(0).toUpperCase()}</div>
        <div className="user-text">
          <span className="welcome-label">Welcome back,</span>
          <span className="user-email">{email}</span>
        </div>
      </div>
      <button onClick={logOut} className="logout-pill">
        <i className="fas fa-sign-out-alt"></i> Log Out
      </button>
    </div>
  );
};

const AddBookForm = () => {
  const navigate = useNavigate();
  const booksPerPage = 12;

  // USER & STATE
  const [userData, setUserData] = useState(() => {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  });

  const [books, setBooks] = useState([]);
  const [editingBookId, setEditingBookId] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [notification, setNotification] = useState('');

  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [ebook, setEbook] = useState(null);
  const [image, setImage] = useState(null);
  const [biographyImage, setBiographyImage] = useState(null);
  const [discountRange, setDiscountRange] = useState('');

  const [salesData, setSalesData] = useState({ 
    totalSales: 0, 
    pendingEarnings: 0, 
    availableEarnings: 0, 
    earnings: 0 
  });
  const [withdrawStatus, setWithdrawStatus] = useState('');
  const [fxPreview, setFxPreview] = useState('');
  const [withdrawals, setWithdrawals] = useState([]); 
  const [loadingMethod, setLoadingMethod] = useState(null);

//self publishing isbn
const [isbn, setIsbn] = useState('');
const [publishingStatus, setPublishingStatus] = useState('self-published');
const [publisherName, setPublisherName] = useState('');

  // WITHDRAWAL MODAL & AMOUNT STATES
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmountUSD, setWithdrawAmountUSD] = useState('');
  const [selectedMethodForWithdraw, setSelectedMethodForWithdraw] = useState('');

  const [withdrawalMethod, setWithdrawalMethod] = useState('bank');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [currency, setCurrency] = useState('KES');
  const [availableBanks, setAvailableBanks] = useState([]);
  const [showBankForm, setShowBankForm] = useState(false);
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [bankMessage, setBankMessage] = useState('');
  const [showInstructions, setShowInstructions] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const discountOptions = [
    { label: '5-10%', value: 0.10 },
    { label: '10-15%', value: 0.15 },
    { label: '15-20%', value: 0.20 },
    { label: '20-30%', value: 0.30 },
  ];

  // INIT
  useEffect(() => {
    if (!userData) navigate("/admin");
    else {
      fetchBooks();
      fetchSalesData();
      fetchSavedWithdrawalDetails();
      fetchWithdrawalHistory();
    }
  }, [userData, navigate]);

  // ====== FETCH FUNCTIONS ======
  const fetchBooks = async () => {
    try {
      const res = await axios.get(`https://www.panafricanbookexchange-wandererspath.com/api/books?userId=${userData.email}`);
      setBooks(res.data);
    } catch (err) { console.error('Error fetching books:', err); }
  };

  const fetchSalesData = async () => {
    try {
      const res = await axios.get(`https://www.panafricanbookexchange-wandererspath.com/api/sales/${userData.email}`);
      setSalesData({
        totalSales: res.data.totalSales || 0,
        pendingEarnings: res.data.pendingEarnings || 0,
        availableEarnings: res.data.availableEarnings || 0,
        earnings: res.data.availableEarnings || 0 
      });
    } catch (err) { 
      console.error('Error fetching sales data:', err); 
    }
  };

  const fetchSavedWithdrawalDetails = async () => {
    try {
      const res = await axios.get(`https://www.panafricanbookexchange-wandererspath.com/api/bank-details/${userData.email}`);
      if (res.data) {
        setWithdrawalMethod(res.data.method);
        setCurrency(res.data.currency || (res.data.method === 'mpesa' ? 'KES' : ''));
      }
    } catch { console.log("No withdrawal method saved yet."); }
  };

  const fetchWithdrawalHistory = async () => {
    try {
      const res = await axios.get(
        `https://www.panafricanbookexchange-wandererspath.com/api/withdraw/history`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setWithdrawals(res.data || []);
    } catch (err) {
      console.error("Error fetching withdrawal history:", err);
      setWithdrawals([]);
    }
  };

  const fetchBanks = async (currencyValue, method) => {
    if (!currencyValue || method !== 'bank') return;
    try {
      const typeParam = method === 'mpesa' ? 'mobile_money' : '';
      const url = `https://www.panafricanbookexchange-wandererspath.com/api/paystack/banks?currency=${currencyValue}${typeParam ? `&type=${typeParam}` : ''}`;
      const res = await axios.get(url);
      setAvailableBanks(res.data.data);
      setBankCode("");
    } catch (err) { console.error("Error fetching banks:", err); setAvailableBanks([]); }
  };

  useEffect(() => {
    if (currency && withdrawalMethod === 'bank' && showBankForm) fetchBanks(currency, withdrawalMethod);
  }, [currency, withdrawalMethod, showBankForm]);

  // ====== FX PREVIEW ======
  const previewFxRate = async () => {
    const inputVal = parseFloat(withdrawAmountUSD);
    if (isNaN(inputVal) || inputVal <= 0 || !currency) {
      setFxPreview('');
      return;
    }
    const netUsd = (inputVal * 0.90) - 0.77;
    if (netUsd <= 0) return setFxPreview("Amount too low after $0.77 fee.");
    try {
      const API_KEY = process.env.REACT_APP_CURRENCY_API_KEY;
      const url = `https://api.currencyapi.com/v3/latest?apikey=${API_KEY}&base_currency=USD&currencies=${currency}`;
      const res = await axios.get(url);
      const rate = res.data.data[currency]?.value;
      if (rate) {
        const estLocal = (netUsd * rate).toFixed(2);
        setFxPreview(`Est. Net Payout: ~${estLocal} ${currency} (1 USD = ${rate.toFixed(2)} ${currency})`);
      } else {
        setFxPreview("Rate unavailable.");
      }
    } catch { setFxPreview("Exchange rate service unavailable."); }
  };

  useEffect(() => { previewFxRate(); }, [withdrawAmountUSD, currency]);

 // ====== BOOK CRUD ======
const handleSubmit = async (e) => {
  e.preventDefault();
  const formData = new FormData();
  formData.append('title', title);
  formData.append('author', author);
  formData.append('price', Math.round(parseFloat(price) * 100)); // Ensure integer cents
  formData.append('category', category);
  formData.append('discountRange', discountRange);
  formData.append('userId', userData.email);

  // Added ISBN and Publishing details
  formData.append('isbn', isbn);
  formData.append('publishingStatus', publishingStatus);
  if (publishingStatus === 'published') {
    formData.append('publisherName', publisherName);
  }

  if (ebook) formData.append('ebook', ebook);
  if (image) formData.append('image', image);
  if (biographyImage) formData.append('biographyImage', biographyImage);

  try {
    if (editingBookId) {
      await axios.put(`https://www.panafricanbookexchange-wandererspath.com/api/books/${editingBookId}`, formData);
      setNotification('Book updated successfully!');
    } else {
      await axios.post('https://www.panafricanbookexchange-wandererspath.com/api/books', formData);
      setNotification('Book added successfully!');
    }
    resetForm();
    fetchBooks(); 
    fetchSalesData();
  } catch (err) { 
    setNotification(err.response?.data?.message || 'Error adding/updating book.'); 
  }
};



const resetForm = () => {
  setTitle(''); 
  setAuthor(''); 
  setPrice(''); 
  setCategory('');
  setDiscountRange(''); 
  setIsbn('');
  setPublishingStatus('self-published');
  setPublisherName('');
  setEbook(null); 
  setImage(null); 
  setBiographyImage(null);
  setEditingBookId(null); 
  setIsFormVisible(false);
};
const handleDelete = async (bookId) => {
    if (!window.confirm("Are you sure you want to delete this book?")) return;
    try { 
      await axios.delete(`https://www.panafricanbookexchange-wandererspath.com/api/books/${bookId}`);
      setNotification('Book deleted successfully!');
      fetchBooks();
      fetchSalesData();
    } catch (err) {
      setNotification(err.response?.data?.message || 'Error deleting book.');
    }
  };
const handleEdit = (book) => {
  setTitle(book.title || ''); 
  setAuthor(book.author || ''); 
  setPrice(book.price ? (book.price / 100).toString() : '');
  setCategory(book.category || ''); 
  setDiscountRange(book.discountRange || '');
  setIsbn(book.isbn || '');
  setPublishingStatus(book.publishingStatus || 'self-published');
  setPublisherName(book.publisherName || '');
  setEditingBookId(book._id); 
  setIsFormVisible(true);
};
  // ====== BANK/M-PESA DETAILS ======
  const handleBankDetailsSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { email: userData.email, method: withdrawalMethod };

      if (withdrawalMethod === 'bank') {
        if (!bankName || !accountNumber || !bankCode || !currency) {
          return setBankMessage("All bank fields required.");
        }
        payload.name = bankName; 
        payload.accountNumber = accountNumber; 
        payload.bankCode = bankCode;
        payload.currency = currency; 
        payload.type = currency === "NGN" ? "nuban" : "mobile_money";
      } else {
        if (!bankName || !mpesaPhone) {
          return setBankMessage("Missing M-Pesa details.");
        }
        let cleanedPhone = mpesaPhone.replace(/\D/g, '');
        if (cleanedPhone.startsWith('0')) {
          cleanedPhone = '254' + cleanedPhone.slice(1);
        } else if (!cleanedPhone.startsWith('254')) {
          cleanedPhone = '254' + cleanedPhone;
        }

        if (!/^254(7|1)\d{8}$/.test(cleanedPhone)) {
          return setBankMessage("Invalid M-Pesa number. Format: 2547XXXXXXXX");
        }

        payload.name = bankName;
        payload.accountNumber = cleanedPhone;
        payload.mobileNumber = cleanedPhone;
        payload.bankCode = 'MPESA'; 
        payload.currency = 'KES'; 
        payload.type = 'mobile_money';
      }

      await axios.post('https://www.panafricanbookexchange-wandererspath.com/api/bank-details', payload);
      setBankMessage("Withdrawal details saved successfully.");
      fetchSavedWithdrawalDetails();

      setTimeout(() => { 
        setBankMessage(''); 
        setShowBankForm(false); 
      }, 1500);

    } catch (err) {
      console.error("Save Error:", err.response?.data || err.message);
      setBankMessage("Failed to save withdrawal details.");
    }
  };

  // ====== WITHDRAWAL LOGIC ======
  const handleWithdrawalValidation = async (clickedMethod) => {
    try {
      const res = await axios.get(`https://www.panafricanbookexchange-wandererspath.com/api/bank-details/${userData.email}`);
      const savedDetails = res.data;
      if (!savedDetails || !savedDetails.method) { setShowBankForm(true); return; }
      if (clickedMethod !== savedDetails.method) {
        if (window.confirm(`You clicked ${clickedMethod}, but saved method is ${savedDetails.method}. Update?`)) {
          setWithdrawalMethod(clickedMethod); setShowBankForm(true); return;
        } else return;
      }
      setSelectedMethodForWithdraw(clickedMethod);
      setWithdrawAmountUSD('');
      setShowWithdrawModal(true);
    } catch { alert("Could not verify withdrawal details."); }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    const requestedUsd = parseFloat(withdrawAmountUSD);

    if (isNaN(requestedUsd) || requestedUsd <= 0) {
      alert("Please enter a valid USD amount.");
      return;
    }

    if (requestedUsd > salesData.availableEarnings) {
      alert(`Amount exceeds available balance of $${salesData.availableEarnings.toFixed(2)} USD.`);
      return;
    }

    if (loadingMethod) return;
    setWithdrawStatus("");
    setLoadingMethod(selectedMethodForWithdraw);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setWithdrawStatus("Please log in again.");
        return;
      }

      const payload = { amount: requestedUsd };
      const res = await axios.post(
        "https://www.panafricanbookexchange-wandererspath.com/api/withdraw",
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowWithdrawModal(false);
      alert(res.data.message || "Withdrawal initiated successfully.");
      setWithdrawStatus("Withdrawal initiated. Check history for updates.");

      await fetchSalesData(); 
      await fetchWithdrawalHistory();
      setFxPreview("");

    } catch (err) {
      console.error("Withdrawal error:", err.response?.data || err.message);
      setWithdrawStatus(err.response?.data?.message || "Withdrawal failed.");
    } finally {
      setLoadingMethod(null);
    }
  };

  // ====== FILTER & PAGINATION ======
  const filteredBooks = (books || []).filter(book => {
    const title = book?.title?.toLowerCase() || "";
    const author = book?.author?.toLowerCase() || "";
    const search = searchTerm?.toLowerCase() || "";
    return title.includes(search) || author.includes(search);
  });

  const totalPages = Math.ceil(filteredBooks.length / booksPerPage) || 1;
  const paginatedBooks = filteredBooks.slice((currentPage - 1) * booksPerPage, currentPage * booksPerPage);

  const availableNetUsd = Math.max(0, (salesData.availableEarnings * 0.90) - 0.77);

  return (
    <div className="add-book-form-container">
      {userData && <DisclaimerPopup userData={userData} />}
      {notification && <div className="notification">{notification}</div>}

      {showInstructions && (
        <div className="instruction-overlay">
          <div className="instruction-popup enriched">
            <div className="instruction-header">
              <div className="icon-badge"></div>
              <h2>AUTHOR PUBLISHING GUIDE {new Date().getFullYear()}</h2>
              <p>Everything you need to know about uploading and getting paid.</p>
            </div>
            <div className="instruction-grid">
              <section className="instr-section">
                <h4><i className="fas fa-file-upload"></i> 1. Content Prep</h4>
                <ul>
                  <li><strong>Files:</strong> &nbsp; PDF eBooks only (Max 50MB).</li>
                  <li><strong>Images:</strong> &nbsp;High-res JPG/PNG for covers and bio.</li>
                  <li><strong>Pricing:</strong> &nbsp;Set your retail price in USD.</li>
                </ul>
              </section>
              <section className="instr-section">
                <h4><i className="fas fa-chart-line"></i> 2. Earnings & Clearing</h4>
                <ul>
                  <li><strong>Commission:</strong> &nbsp;You keep 90% of every sale.</li>
                  <li><strong>Verification:</strong> &nbsp;Sales stay "Pending" for 24 hours to clear.</li>
                  <li><strong>Notifications:</strong> &nbsp;You will receive an email alert for every sale and withdrawal.</li>
                </ul>
              </section>
              <section className="instr-section">
                <h4><i className="fas fa-wallet"></i> 3. Withdrawals</h4>
                <ul>
                  <li><strong>Processing Fee:</strong> &nbsp; A flat USD 0.77 fee applies per payout.</li>
                  <li><strong>Minimum:</strong> &nbsp;Net payout must be at least USD 1.00.</li>
                  <li><strong>Methods:</strong> &nbsp; Choose between M-Pesa or Bank Transfer.</li>
                </ul>
              </section>
              <section className="instr-section timing-note">
                <h4><i className="fas fa-clock"></i> 4. Payout Timing</h4>
                <p>
                  M-Pesa withdrawals are processed instantly once available. 
                  Bank Transfers typically arrive within 2 to 7 business days.
                </p>
              </section>
            </div>
            <button className="btn-get-started" onClick={() => setShowInstructions(false)}>
              Agree & Continue
            </button>
          </div>
        </div>
      )}

      <div className="sales-info">
        <h3 style={{ borderBottom: '2px solid #eee', paddingBottom: '10px' }}>Revenue Dashboard</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', margin: '15px 0' }}>
          <div className="stat-card">
            <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Total Gross Sales</p>
            <h3 style={{ margin: 0 }}>${(salesData.totalSales || 0).toFixed(2)}</h3>
          </div>
          <div className="stat-card">
            <p style={{ margin: 0, fontSize: '14px', color: '#ffa500' }}>Pending (Clear in 24h)</p>
            <h3 style={{ margin: 0, color: '#ffa500' }}>${((salesData.pendingEarnings || 0) * 0.9).toFixed(2)}</h3>
          </div>
        </div>

        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#28a745' }}>Available for Withdrawal</h4>
          <p style={{ margin: '5px 0' }}>
            Gross Available: ${(salesData.availableEarnings || 0).toFixed(2)} USD
          </p>
          <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
            Net After $0.77 Fee & 10% Cut: <span style={{ fontSize: '1.2rem' }}>
              ${availableNetUsd.toFixed(2)} USD
            </span>
          </p>
        </div>

        <div className="withdrawal-actions" style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn"
            onClick={() => handleWithdrawalValidation('mpesa')}
            disabled={loadingMethod !== null || availableNetUsd < 1.0}
            style={{ backgroundColor: '#28a745', color: '#fff', flex: 1, opacity: availableNetUsd < 1.0 ? 0.6 : 1 }}
          >
            {loadingMethod === "mpesa" ? "Processing..." : "Withdraw via M-Pesa"}
          </button>
          <button
            className="btn"
            onClick={() => handleWithdrawalValidation('bank')}
            disabled={loadingMethod !== null || availableNetUsd < 1.0}
            style={{ backgroundColor: '#007bff', color: '#fff', flex: 1, opacity: availableNetUsd < 1.0 ? 0.6 : 1 }}
          >
            {loadingMethod === "bank" ? "Processing..." : "Withdraw to Bank"}
          </button>
        </div>

        {availableNetUsd < 1.0 && (
          <p style={{ fontSize: '12px', color: '#dc3545', marginTop: '10px' }}>
            * Minimum withdrawal is $1.00 net after fees.
          </p>
        )}

        {withdrawStatus && (
          <p style={{ 
            marginTop: '10px', 
            padding: '10px',
            borderRadius: '5px',
            backgroundColor: withdrawStatus.toLowerCase().includes('initiated') ? '#e6fffa' : '#fff5f5',
            color: withdrawStatus.toLowerCase().includes('initiated') ? 'green' : 'red' 
          }}>
            {withdrawStatus}
          </p>
        )}
      </div>

      {/* WITHDRAW AMOUNT SELECTION MODAL */}
      {showWithdrawModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close-button" onClick={() => setShowWithdrawModal(false)}>&times;</span>
            <h3>Initiate Payout ({selectedMethodForWithdraw.toUpperCase()})</h3>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Available Balance: <strong>${salesData.availableEarnings.toFixed(2)} USD</strong>
            </p>
            
            <form onSubmit={handleWithdraw} className="styled-form">
              <label style={{ fontSize: '14px', fontWeight: 'bold' }}>Enter Amount to Withdraw (USD):</label>
              <input 
                type="number" 
                step="0.01" 
                min="1.00" 
                max={salesData.availableEarnings}
                placeholder={`e.g. ${salesData.availableEarnings.toFixed(2)}`} 
                value={withdrawAmountUSD} 
                onChange={(e) => setWithdrawAmountUSD(e.target.value)} 
                required 
              />

              {fxPreview && (
                <p style={{ fontSize: '13px', color: '#28a745', fontWeight: 'bold', margin: '5px 0' }}>
                  {fxPreview}
                </p>
              )}

              <button type="submit" className="btn btn-primary" disabled={loadingMethod !== null}>
                {loadingMethod ? "Submitting..." : "Confirm & Send Payout"}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="history-trigger-container">
  </div>


      {showHistory && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Withdrawal History</h3>
              <span className="close-button" onClick={() => setShowHistory(false)}>&times;</span>
            </div>
            <div className="modal-body">
              {withdrawals.length === 0 ? (
                <div className="no-data"><p>No withdrawals found yet.</p></div>
              ) : (
                <div className="table-responsive">
                  <table className="withdrawal-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Method</th>
                        <th>USD</th>
                        <th>Local Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {withdrawals.map((w, i) => (
                        <tr key={i}>
                          <td>{new Date(w.createdAt).toLocaleDateString()}</td>
                          <td><span className={`method-badge ${w.method}`}>{w.method?.toUpperCase()}</span></td>
                          <td className="fw-bold">${w.amountUSD?.toFixed(2)}</td>
                          <td>{w.convertedAmount?.toFixed(2)} {w.localCurrency}</td>
                          <td><span className={`status-pill ${w.status}`}>{w.status?.toUpperCase()}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <button className="btn btn-outline-info" style={{ marginTop: '10px' }} onClick={() => setShowBankForm(true)}>Update Bank/M-Pesa Details</button>

      {showBankForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close-button" onClick={() => setShowBankForm(false)}>&times;</span>
            <h3>Enter Your Withdrawal Details</h3>
            <form onSubmit={handleBankDetailsSubmit} className="styled-form">
              <select value={withdrawalMethod} onChange={(e) => setWithdrawalMethod(e.target.value)} required>
                <option value="bank">Bank Account</option>
                <option value="mpesa">M-Pesa</option>
              </select>
              <input type="text" placeholder="Account Name" value={bankName} onChange={(e) => setBankName(e.target.value)} required />
              {withdrawalMethod === 'bank' ? (
                <>
                  <input type="text" placeholder="Account Number" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} required />
                  <select value={currency} onChange={(e) => { setCurrency(e.target.value); fetchBanks(e.target.value, withdrawalMethod); }} required>
                    <option value="">Select Currency</option>
                    <option value="USD">USD</option>
                    <option value="NGN">NGN</option>
                    <option value="KES">KES</option>
                    <option value="GHS">GHS</option>
                    <option value="ZAR">ZAR</option>
                  </select>
                  <select value={bankCode} onChange={(e) => setBankCode(e.target.value)} required disabled={!availableBanks.length}>
                    <option value="">Select Bank</option>
                    {availableBanks.map((bank) => (<option key={bank.code} value={bank.code}>{bank.name}</option>))}
                  </select>
                </>
              ) : (
                <>
                  <input type="tel" placeholder="M-Pesa Number (+2547XXXXXXXX)" value={mpesaPhone} onChange={(e) => setMpesaPhone(e.target.value)} required />
                  <input type="text" value="Currency: KES" readOnly />
                  <input type="text" value="Provider: Safaricom" readOnly />
                </>
              )}
              <button type="submit" className="btn btn-primary">Save Details</button>
            </form>
            {bankMessage && <p className="bank-message">{bankMessage}</p>}
          </div>
        </div>
      )}

      <input type="search" placeholder="Search by title or author..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" style={{ marginTop: '20px' }} />
      <br />
      <button onClick={() => setIsFormVisible(!isFormVisible)} className="add-book-button">
        {isFormVisible ? 'Cancel' : 'Add Book'}
      </button>





{isFormVisible && (
  <form onSubmit={handleSubmit} encType="multipart/form-data" className="book-form">
    <input type="text" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
    <input type="text" placeholder="Author" value={author} onChange={(e) => setAuthor(e.target.value)} required />
    <input type="number" step="0.01" placeholder="Price (USD)" value={price} onChange={(e) => setPrice(e.target.value)} required />
    
    <select value={category} onChange={(e) => setCategory(e.target.value)} required>
      <option value="">Select Category</option>
      <option value="Fiction">Fiction</option>
      <option value="Non-Fiction">Non-Fiction</option>
    </select>
    
    <select value={discountRange} onChange={(e) => setDiscountRange(e.target.value)} required>
      <option value="">Select Discount Range</option>
      {discountOptions.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
    </select>
    
    {/* ====== AUTHOR VERIFICATION: ISBN ====== */}
    <label className="label">ISBN (Author Verification)</label>
    <input 
      type="text" 
      placeholder="e.g. 978-3-16-148410-0" 
      value={isbn} 
      onChange={(e) => setIsbn(e.target.value)} 
      pattern="^(?=(?:\D*\d){10}(?:(?:\D*\d){3})?$)[\d-]+$"
      title="Enter a valid 10 or 13-digit ISBN number"
      required 
    />

    {/* ====== PUBLISHING STATUS ====== */}
    <label className="label">Publishing Status</label>
    <select value={publishingStatus} onChange={(e) => setPublishingStatus(e.target.value)} required>
      <option value="self-published">Self-Published</option>
      <option value="published">Published via Publisher</option>
    </select>

    {/* ====== CONDITIONAL PUBLISHER NAME INPUT ====== */}
    {publishingStatus === 'published' && (
      <input 
        type="text" 
        placeholder="Publisher Name" 
        value={publisherName} 
        onChange={(e) => setPublisherName(e.target.value)} 
        required 
      />
    )}

    {/* File inputs made optional during Edit mode */}
    <label className="label">Upload eBook PDF {editingBookId && '(Leave blank to keep current)'}</label>
    <input type="file" onChange={(e) => setEbook(e.target.files[0])} accept=".pdf" required={!editingBookId} />
    
    <label className="label">Upload eBook Cover {editingBookId && '(Leave blank to keep current)'}</label>
    <input type="file" onChange={(e) => setImage(e.target.files[0])} accept="image/*" required={!editingBookId} />
    
    <label className="label">Upload Author Biography Image</label>
    <input type="file" onChange={(e) => setBiographyImage(e.target.files[0])} accept="image/*" />
    
    <button type="submit" className="submit-button">{editingBookId ? 'Update Book' : 'Add Book'}</button>
  </form>
)}



      <div className="book-grid">
        {paginatedBooks.map(book => (
          <div key={book._id} className="book-card">
            {book.imagePath && (
              <div className="book-image-wrapper">
                <img 
                  src={`https://www.panafricanbookexchange-wandererspath.com/${book.imagePath}`} 
                  alt={book.title} 
                  className="book-image" 
                />
              </div>
            )}
            <div className="book-card-content">
              <h3>{book.title}</h3>
              <p className="author-text"><span>Author:</span> {book.author}</p>
              <p className="category-text"><span>Category:</span> {book.category}</p>
              <p className="price-text">${(book.price / 100).toFixed(2)}</p>
              <div className="book-card-actions">
                <button onClick={() => handleEdit(book)} className="btn-edit">Edit</button>
                <button onClick={() => handleDelete(book._id)} className="btn-delete">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="pagination-container">
        <div className="pagination-controls">
          <button 
            className="pagination-btn nav-btn" 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
            disabled={currentPage === 1}
          >
            &laquo; Prev
          </button>
          <div className="pagination-numbers">
            {Array.from({ length: totalPages }, (_, index) => (
              <button 
                key={index + 1} 
                onClick={() => setCurrentPage(index + 1)} 
                className={`pagination-btn number-btn ${currentPage === index + 1 ? 'active' : ''}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <button 
            className="pagination-btn nav-btn" 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
            disabled={currentPage === totalPages}
          >
            Next &raquo;
          </button>
        </div>
        <p className="pagination-info">
          Page {currentPage} of {totalPages}
        </p>
      </div>
    </div>
  );
};

export default AddBookForm;